<template>
  <div>
    <ProfileListAddv2
      :createProfileActive.sync="createProfileActive"
      :selectedStudent="studentData"
      :redirectOnComplete="false"
      @refetch-data="refetchData"
      v-if="$can('roster.profile.create')"
    />

    <b-card no-body class="mb-0 table-no-top table-text-nowrap">
      <b-table
        ref="refProfileListTable"
        class="position-relative mb-25"
        :items="fetchProfiles"
        responsive
        :fields="tableColumnsStudentView"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No profiles assigned to this user"
        :sort-desc.sync="isSortDirDesc"
        @row-contextmenu="onRowContextMenu"
        @refreshed="sectionListVersion += 1"
      >
        <!-- Column: Session -->
        <template #cell(session)="data">
          <div class="d-flex align-items-center">
            <div @click="data.toggleDetails" style="margin-left: -5px" class="mr-50 cursor-pointer" v-if="data.item.session">
              <feather-icon icon="ChevronDownIcon" size="24" v-if="data.detailsShowing" />
              <feather-icon icon="ChevronRightIcon" size="24" v-if="!data.detailsShowing" />
            </div>
            <div class="text-nowrap" v-if="data.item.session">
              <div
                v-if="data.item.session.type === 'pathway' && data.item.session.pathway"
                class="d-flex align-items-center text-nowrap"
                style="gap: 0.625rem"
              >
                <b-avatar
                  size="36"
                  :src="data.item.session.pathway.avatar"
                  :text="avatarText(data.item.session.pathway.name)"
                  :variant="`light-info`"
                  :to="{ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.session.id } }"
                  style="margin-top: 2px"
                  rounded
                />
                <div class="d-flex flex-column" style="gap: 0">
                  <div class="d-flex align-items-center font-weight-bold text-nowrap text-white mb-0">
                    <div
                      class="cursor-pointer"
                      @click="$router.push({ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.session.id } })"
                    >
                      {{ resolvePathwayCohortName(data.value) }}
                    </div>
                    <div v-if="data.item.reassignFrom" class="ml-50">
                      <feather-icon
                        @click="redirectToReassigned(data.item.reassignFrom)"
                        icon="InfoIcon"
                        class="text-warning cursor-pointer"
                        :id="`sr-${data.item.id}-reassign-${data.item.reassignFrom.id}-tooltip`"
                        style="margin-bottom: 2px; z-index: 9"
                      />
                      <b-tooltip
                        placement="right"
                        @click="redirectToReassigned(data.item.reassignFrom)"
                        :title="`Reassigned From ${data.item.reassignFrom.name}`"
                        class="cursor-pointer"
                        :target="`sr-${data.item.id}-reassign-${data.item.reassignFrom.id}-tooltip`"
                      />
                    </div>
                  </div>
                  <b-link
                    :to="{ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.session.id } }"
                    class="d-flex align-items-center text-muted text-nowrap"
                  >
                    <lucide-icon name="Library" style="width: 14px; height: 14px; margin-right: 1px" />
                    <small> {{ data.item.session.pathway.name }}</small>
                  </b-link>
                </div>
              </div>
              <div
                v-if="data.item.session.type === 'course' && data.item.session.course"
                class="d-flex align-items-center text-nowrap"
                style="gap: 0.625rem"
              >
                <b-avatar
                  size="36"
                  :src="data.item.session.course.avatar"
                  :text="avatarText(data.item.session.course.name)"
                  :variant="`light-info`"
                  :to="{ name: 'apps-roster-course-section-view', params: { id: data.item.session.id } }"
                  style="margin-top: 2px"
                  rounded
                />
                <div class="d-flex flex-column" style="gap: 0">
                  <div class="d-flex align-items-center font-weight-bold text-nowrap text-white mb-0">
                    <div
                      class="cursor-pointer"
                      @click="$router.push({ name: 'apps-roster-course-section-view', params: { id: data.item.session.id } })"
                    >
                      {{ resolveCourseSectionName(data.value) }}
                    </div>
                    <div v-if="data.item.reassignFrom" class="ml-50">
                      <feather-icon
                        @click="redirectToReassigned(data.item.reassignFrom)"
                        icon="InfoIcon"
                        class="text-warning cursor-pointer"
                        :id="`sr-${data.item.id}-reassign-${data.item.reassignFrom.id}-tooltip`"
                        style="margin-bottom: 2px; z-index: 9"
                      />
                      <b-tooltip
                        @click="redirectToReassigned(data.item.reassignFrom)"
                        placement="right"
                        :title="`Reassigned From ${data.item.reassignFrom.name}`"
                        class="cursor-pointer"
                        :target="`sr-${data.item.id}-reassign-${data.item.reassignFrom.id}-tooltip`"
                      />
                    </div>
                  </div>
                  <b-link
                    :to="{ name: 'apps-roster-course-section-view', params: { id: data.item.session.id } }"
                    class="d-flex align-items-center text-muted text-nowrap"
                  >
                    <lucide-icon name="Book" style="width: 14px; height: 14px; margin-right: 1px" />
                    <small> {{ data.item.session.course.name }}</small>
                  </b-link>
                </div>
              </div>
            </div>
            <div class="text-nowrap d-flex align-items-center" v-else>
              <feather-icon
                :id="`profile-row-${data.item.id}-set-session-icon`"
                icon="PlusCircleIcon"
                class="cursor-pointer mr-25"
                size="16"
                @click="startSessionSelect(data.item)"
              />

              <b-tooltip title="Assign Session" class="cursor-pointer" :target="`profile-row-${data.item.id}-set-session-icon`" />

              Not Enrolled
            </div>
          </div>
        </template>

        <!-- Column: Session -->
        <template #cell(email)="data">
          <span class="text-nowrap">{{ data.item.student.email }}</span>
        </template>

        <!-- Column: Attributes - Program -->
        <template #cell(fundingSource)="data">
          <profile-field-select
            :profile="data.item"
            attribute="fundingSource"
            name="funding"
            :options="fundingSourceOptions"
            :resolve-variant="resolveProfileProgramVariant"
          />
        </template>

        <!-- Column: Attributes - Payment Status -->
        <template #cell(paymentStatus)="data">
          <b-badge
            :variant="`${resolveProfilePaymentStatusVariant(data.item.attributes.paymentStatus)}`"
            class="text-nowrap text-capitalize"
          >
            {{ title(data.item.attributes.paymentStatus) }}
          </b-badge>
        </template>

        <!-- Column: Attributes - Uploaded Resume -->
        <template #cell(uploadedResume)="data">
          <div class="text-nowrap">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-resume-icon`"
              icon="UploadIcon"
              class="cursor-pointer"
              size="16"
              @click="startUpload(data.item, 'enrollment', 'resume')"
            />

            <b-tooltip title="Upload Resume" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-resume-icon`" />
            &nbsp;
            <profile-field-select
              :profile="data.item"
              attribute="uploadedResume"
              name="resume status"
              :options="uploadedResumeOptions"
              :resolve-variant="resolveProfileYNVariant"
            />
          </div>
        </template>

        <!-- Column: Attributes - Uploaded COE -->
        <template #cell(uploadedCoe)="data">
          <div class="text-nowrap">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-coe-icon`"
              icon="UploadIcon"
              class="cursor-pointer"
              size="16"
              @click="startUpload(data.item, 'enrollment', 'coe')"
            />

            <b-tooltip title="Upload COE" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-coe-icon`" />
            &nbsp;
            <profile-field-select
              :profile="data.item"
              attribute="uploadedCoe"
              name="COE status"
              :options="uploadedCoeOptions"
              :resolve-variant="resolveProfileYNVariant"
            />
          </div>
        </template>

        <!-- Column: Attributes - Uploaded Transcript -->
        <template #cell(uploadedTranscript)="data">
          <div class="text-nowrap">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-transcript-icon`"
              icon="UploadIcon"
              class="cursor-pointer"
              size="16"
              @click="startUpload(data.item, 'transcript', 'prior')"
            />

            <b-tooltip title="Upload Transcript" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-transcript-icon`" />
            &nbsp;
            <profile-field-select
              :profile="data.item"
              attribute="uploadedTranscript"
              name="transcript status"
              :options="uploadedTranscriptOptions"
              :resolve-variant="resolveProfileYNVariant"
            />
          </div>
        </template>

        <!-- Column: Attributes - VA Submission -->
        <template #cell(submittedToVa)="data">
          <profile-submitted-to-va-select :profile="data.item" />
        </template>

        <!-- Column: Session -->
        <template #cell(createdAt)="data">
          {{ momentTz(data.item.createdAt).format('L') }}
        </template>

        <!-- Column: Retake -->
        <template #cell(retake)="data">
          <profile-field-select
            :profile="data.item"
            attribute="retake"
            name="retake"
            :options="yesNoActualOptions"
            :resolve-variant="resolveProfileYNVariant"
            :light="false"
            @updated="refetchData"
          />
        </template>

        <!-- Column: Modality -->
        <template #cell(modality)="data">
          <profile-field-select
            :profile="data.item"
            attribute="location"
            name="modality"
            :options="locationOptions"
            :resolve-variant="resolveProfileLocationStatus"
            :light="false"
          />
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <profile-field-select
            :profile="data.item"
            field="status"
            name="status"
            :options="editableStatusOptions"
            :resolve-variant="resolveProfileStatusVariant"
            :light="false"
            @updated="refetchData"
          />
        </template>

        <!-- Column: Transfer of Credit -->
        <template #cell(toc)="data">
          <action-badge-dropdown
            :value="data.item.toc && data.item.toc.enabled ? 'yes' : 'no'"
            :options="yesNoActualOptions"
            :resolve-variant="resolveProfileYNVariant"
            :disabled="!data.item.session || !$can('roster.cohort.student.credit')"
            @select="(value) => openCourseToCModal(data.item, value === 'yes')"
          />
        </template>

        <!-- Column: Compliance Score -->
        <template #cell(compliance)="data">
          <template v-if="!data.item.compliance || !data.item.compliance.scores || !data.item.session">
            <b-badge variant="danger">N/A</b-badge>
          </template>

          <template v-else>
            <b-badge
              :variant="`${resolveComplianceScoreVariant({
                score: data.item.compliance.scores.overall,
              })}`"
              :id="`compliance-${data.item.id}`"
              class="text-capitalize"
              style="cursor: help"
            >
              {{ gradeScore({ score: data.item.compliance.scores.overall, percent: true, status: data.item.status }) }}
              <font-awesome-icon v-if="data.item.compliance.locked" icon="fa-regular fa-lock" />
            </b-badge>

            <compliance-popover
              :target-id="data.item.id"
              :student-name="data.item.student.firstName"
              :compliance-data="data.item.compliance"
              :sections-data="data.item.session.sections"
              :profile-data="data.item"
            />
          </template>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="primary" class="dropdown-edit-button" :popper-opts="{ positionFixed: true }">
            <template #button-content>
              <div class="p-50">
                <feather-icon icon="MoreHorizontalIcon" size="21" />
              </div>
            </template>

            <b-dropdown-item
              v-if="$can('roster.profile.update')"
              @click="$router.push({ name: 'apps-roster-profile-edit', params: { id: data.item.id } })"
            >
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Edit</span>
                <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>

            <b-dropdown-item @click="focusProfileDocuments(data.item)">
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Focus - Documents</span>
                <feather-icon icon="FilePlusIcon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>

            <template v-if="data.item.session">
              <b-dropdown-divider />

              <b-dropdown-item @click="downloadAttendanceSheet(data.item.session, data.item)">
                <div class="d-flex align-items-center justify-content-between w-100">
                  <span>Attendance</span>
                  <feather-icon icon="BookOpenIcon" size="15" class="ml-50 text-muted" />
                </div>
              </b-dropdown-item>

              <b-dropdown-item v-if="$can('roster.cohort.student.credit')" @click="openCourseToCModal(data.item, true)">
                <div class="d-flex align-items-center justify-content-between w-100">
                  <span>Add Credit</span>
                  <feather-icon icon="CreditCardIcon" size="15" class="ml-50 text-muted" />
                </div>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('roster.compliance.unlock') && isComplianceLocked(data.item)"
                @click="openComplianceUnlockModal(data.item)"
              >
                <div class="d-flex align-items-center justify-content-between w-100">
                  <span>Unlock Compliance</span>
                  <font-awesome-icon icon="fa-regular fa-lock-open" class="ml-50 text-muted" />
                </div>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="data.item.toc.enabled && $can('roster.cohort.student.credit')"
                @click="openCourseToCModal(data.item, false)"
              >
                <div class="d-flex align-items-center justify-content-between w-100">
                  <span>Remove Credit</span>
                  <feather-icon icon="CreditCardIcon" size="15" class="ml-50 text-muted" />
                </div>
              </b-dropdown-item>

              <b-dropdown-item v-if="$can('roster.cohort.student.remove')" @click="removeFromCohort(data.item.session.id, data.item.id)">
                <div class="d-flex align-items-center justify-content-between w-100">
                  <span>Remove</span>
                  <feather-icon icon="ArchiveIcon" size="15" class="ml-50 text-muted" />
                </div>
              </b-dropdown-item>
            </template>

            <b-dropdown-divider />

            <b-dropdown-item v-if="$can('roster.profile.archive')" @click="archiveProfile(data.item.id)">
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Archive</span>
                <feather-icon icon="ArchiveIcon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </template>

        <!--- Sections for Profile -->
        <template #row-details="row">
          <!--
            Keyed on each profile refresh: rows of a single-profile student come back
            already expanded, so without the key Vue reuses this list with unchanged
            props and it keeps the section statuses it loaded before (DEV-704).
          -->
          <student-relations-roster-profile-section-list
            :key="`${row.item.id}-${sectionListVersion}`"
            :student-id="row.item.student.id"
            :cohort-id="row.item.session.id"
            :profile-id="row.item.id"
            @add-credit="(sectionId) => openCourseToCModal(row.item, true, sectionId)"
            @changed="refetchData"
          />
        </template>
      </b-table>
      <div class="mx-2 mb-75 mt-0">
        <b-row>
          <b-col cols="12" md="3" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="mb-0 text-muted">{{ dataMeta.of }} Total Entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" md="9" class="d-flex align-items-center justify-content-center justify-content-sm-end mt-1 mt-sm-0">
            <div class="mr-2 vue-select-up">
              <span class="mb-0 mr-75">Rows Per Page</span>
              <v-select
                size="s"
                v-model="perPage"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
                :open="true"
              />
            </div>
            <span class="mr-2 ml-lg-50 mb-0">Page {{ currentPage }} of {{ totalPageCount }}</span>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalProfiles"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-sm-0 ml-lg-50"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <vue-context ref="studentRosterProfileMenu" class="mw-8" v-slot="{ data }">
      <template v-if="data">
        <li>
          <a
            v-if="$can('roster.profile.update')"
            class="d-flex align-items-center justify-content-between"
            @click="$router.push({ name: 'apps-roster-profile-edit', params: { id: data.item.id } })"
          >
            <span>Edit</span>
            <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
          </a>
        </li>
        <li>
          <a class="d-flex align-items-center justify-content-between" @click="() => focusProfileDocuments(data.item)">
            <span>Focus - Documents</span>
            <feather-icon icon="FilePlusIcon" size="15" class="ml-50 text-muted" />
          </a>
        </li>

        <li v-if="hasCreditToView(data.item)">
          <a class="d-flex align-items-center justify-content-between" @click="openCourseToCViewModal(data.item)">
            <span class="align-middle">View Credit</span>
            <feather-icon icon="CreditCardIcon" />
          </a>
        </li>

        <li v-if="data.item && data.item.session && $can('roster.cohort.student.credit')">
          <a class="d-flex align-items-center justify-content-between" @click="openCourseToCModal(data.item, true)">
            <span class="align-middle">Add Credit</span>
            <feather-icon icon="CreditCardIcon" />
          </a>
        </li>
        <li v-if="data.item && data.item.session && data.item.toc.enabled && $can('roster.cohort.student.credit')">
          <a class="d-flex align-items-center justify-content-between" @click="openCourseToCModal(data.item, false)">
            <span class="align-middle">Remove Credit</span>
            <feather-icon icon="CreditCardIcon" />
          </a>
        </li>

        <li v-if="$can('compliance.transcript.create')" class="v-context__sub">
          <a> <span> Transcript </span> </a>
          <ul class="v-context">
            <li>
              <b-link
                class="d-flex align-items-center justify-content-between"
                @click="createComplianceTranscript(data.item.id, 'unofficial')"
              >
                <span>Unofficial</span>
              </b-link>
            </li>
            <li>
              <b-link
                class="d-flex align-items-center justify-content-between"
                @click="createComplianceTranscript(data.item.id, 'official')"
              >
                <span>Official</span>
              </b-link>
            </li>
            <li>
              <b-link
                class="d-flex align-items-center justify-content-between"
                @click="createComplianceTranscript(data.item.id, 'official_copy')"
              >
                <span>Official (Copy)</span>
              </b-link>
            </li>
          </ul>
        </li>

        <!--
          Parent and child share one condition on purpose. "Accept" is the only
          item in here, so when the two disagreed the submenu still opened for a
          profile that could not be accepted and rendered an empty panel -- which
          is what ~22% of profiles did, the ones with no metadata.enrollmentId.
          Any further item added here needs the same treatment.
        -->
        <li class="v-context__sub" v-if="canAcceptEnrollment(data.item) || canSendAcceptanceLetter(data.item)">
          <a> <span> Enrollment </span> </a>
          <ul class="v-context">
            <li v-if="canAcceptEnrollment(data.item)">
              <b-link class="d-flex align-items-center justify-content-between" @click="submitEnrollmentAcceptance(data.item)">
                <span>{{ hasEnrollmentAcceptance(data.item) ? 'Re-accept' : 'Accept' }}</span>
                <feather-icon icon="ArchiveIcon" size="15" class="ml-50 text-muted" />
              </b-link>
            </li>
            <li v-if="canSendAcceptanceLetter(data.item)">
              <b-link class="d-flex align-items-center justify-content-between" @click="sendAcceptanceLetter(data.item)">
                <span>{{ acceptanceLetterSentAt(data.item) ? 'Re-send Acceptance Letter' : 'Send Acceptance Letter' }}</span>
                <feather-icon icon="MailIcon" size="15" class="ml-50 text-muted" />
              </b-link>
            </li>
          </ul>
        </li>

        <li class="v-context__sub" v-if="$can('roster.profile.delete')">
          <a> <span> Manage </span> </a>
          <ul class="v-context">
            <li v-if="$can('roster.profile.delete')">
              <b-link class="d-flex align-items-center justify-content-between" @click="archiveProfile(data.item.id)">
                <span>Archive</span>
                <feather-icon icon="ArchiveIcon" size="15" class="ml-50 text-muted" />
              </b-link>
            </li>
          </ul>
        </li>
      </template>
    </vue-context>

    <student-view-credit
      v-if="isViewCreditOpen"
      :is-view-credit-open.sync="isViewCreditOpen"
      :profile="selectedProfile"
      :student="studentData"
    ></student-view-credit>
    <student-add-credit
      v-if="isAddCreditOpen"
      v-on:refresh="refetchData"
      :is-add-credit-open.sync="isAddCreditOpen"
      :selected-profile="selectedProfile"
      :cohort-data="selectedCohort"
      :is-assign="isAssignOp"
      :initial-section-id="selectedCreditSectionId"
    ></student-add-credit>
    <profile-add-to-session :is-add-session-open.sync="assignProfileCohortActive" v-on:refresh="refetchData" :profile="cohortProfile" />

    <compliance-unlock-modal
      :profile="complianceUnlock.profile"
      :sections="complianceUnlock.profile && complianceUnlock.profile.session ? complianceUnlock.profile.session.sections : []"
      :visible="complianceUnlock.isModalOpen"
      @hidden="() => (complianceUnlock.isModalOpen = false)"
      @unlocked="refetchData"
    />
  </div>
</template>

<script>
import ProfileSubmittedToVaSelect from '@/views/apps/roster/profile/components/ProfileSubmittedToVaSelect.vue';
import ProfileFieldSelect from '@/views/apps/roster/profile/components/ProfileFieldSelect.vue';
import ActionBadgeDropdown from '@/views/apps/roster/profile/components/ActionBadgeDropdown.vue';
import {
  statusOptions,
  uploadedCoeOptions,
  uploadedResumeOptions,
  uploadedTranscriptOptions,
  fundingSourceOptions,
  yesNoActualOptions,
  locationOptions,
} from '@/views/apps/roster/profile/profileOptions';

// Mirrors ACCEPTABLE_STATES in mgmt-api services/roster/enrollment-acceptance.js (DEV-738).
const ACCEPTABLE_STATES = ['created', 'pending', 'action_required', 'compliance', 'enrolled'];
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownDivider,
  BDropdownItem,
  BFormInput,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
  BTooltip,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { ref } from '@vue/composition-api';
import fileDownload from 'js-file-download';
import VueContext from 'vue-context';

import store from '@/store';
import { avatarText, gradeScore, title } from '@core/utils/filter';
import { momentTz } from '@/libs/utils/time';

import FormattedTime from '@/@core/components/time/FormattedTime.vue';
import useProfiles from '@/views/apps/roster/profile/useProfiles';
import useUsersList from '@/views/apps/audit/audit-list/useAuditList';
import usePathways from '@/views/apps/roster/pathways/usePathways';
import useCourses from '@/views/apps/roster/courses/useCourses';
import usePathwayCohorts from '@/views/apps/roster/pathways/cohorts/usePathwayCohorts';
import useCourseSections from '@/views/apps/roster/courses/sections/useCourseSections';
import StudentRelationsRosterProfileSectionList from './StudentRelationsRosterProfileSectionList.vue';
import ProfileAddToSession from '@/views/apps/roster/profile/list/components/ProfileAddToSession.vue';
import ProfileListAddv2 from '@/views/apps/roster/profile/create/ProfileListAddv2.vue';
import StudentAddCredit from '@/views/apps/student/students-view/components/StudentAddCredit.vue';
import StudentViewCredit from '@/views/apps/student/students-view/components/StudentViewCredit.vue';
import CompliancePopover from '@/views/apps/roster/courses/sections/view/components/CompliancePopover.vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import ComplianceUnlockModal from '@/views/apps/roster/profile/components/ComplianceUnlockModal.vue';

export default {
  components: {
    ProfileSubmittedToVaSelect,
    ProfileFieldSelect,
    ActionBadgeDropdown,
    ComplianceUnlockModal,
    CompliancePopover,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    BTooltip,

    FormattedTime,

    vSelect,
    VueContext,

    StudentRelationsRosterProfileSectionList,
    ProfileAddToSession,
    ProfileListAddv2,
    StudentAddCredit,
    StudentViewCredit,
  },
  props: {
    studentData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      complianceUnlock: {
        isModalOpen: false,
        profile: undefined,
      },
      uploadedCoeOptions,
      uploadedResumeOptions,
      uploadedTranscriptOptions,
      fundingSourceOptions,
      yesNoActualOptions,
      locationOptions,
      selectedCreditSectionId: undefined,
      sectionListVersion: 0,
      // Archiving has its own confirmed action and cannot be undone, so it is not offered inline.
      editableStatusOptions: statusOptions.filter((option) => option.value !== 'archived'),
    };
  },
  methods: {
    isComplianceLocked(profile) {
      if (!profile || !profile.compliance) return false;
      if (profile.compliance.locked) return true;

      // The document-level flag is only written on an unlock-everything, so a profile
      // can carry locked sections without it set. Gating on it alone would hide the
      // action from exactly the profiles that need it.
      const sections = profile.compliance.sections || {};
      return Object.keys(sections).some((id) => sections[id].locked);
    },
    openComplianceUnlockModal(profile) {
      this.complianceUnlock.profile = profile;
      this.complianceUnlock.isModalOpen = true;
    },
    redirectToReassigned(cohort) {
      if (cohort.type === 'course' && Array.isArray(cohort.sections) && cohort.sections.length > 0) {
        const id = cohort.sections[0].id ? cohort.sections[0].id : cohort.sections[0];
        return this.$router.push(`/roster/courses/sections/${id}`);
      }
      if (cohort.type === 'pathway') {
        const id = cohort.id ? cohort.id : cohort._id;
        return this.$router.push(`/roster/pathways/cohorts/${id}`);
      }
    },
    createNewProfile() {
      this.createProfileActive = true;
    },
    startSessionSelect(profile) {
      this.cohortProfile = profile;
      this.assignProfileCohortActive = true;
    },
    onRowContextMenu(item, index, event) {
      console.log('do::', item);
      this.$refs.studentRosterProfileMenu.open(event, { item });
      event.preventDefault();
    },
    focusProfileDocuments(profile) {
      this.$emit('focus-document', { profileId: profile.id });
    },
    async removeFromCohort(cohortId, profileId) {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to remove this student from this cohort?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          const response = await this.$store
            .dispatch('rosterStoreModule/removeStudentsFromPathwayCohort', {
              cohortId,
              profileIds: [profileId],
            })
            .then(() => {
              this.refetchData();
            })
            .catch((error) => {
              console.error('failed to remove students to class', error);
            });
        }
      });
    },
    archiveProfile(profileId) {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to archive this profile?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          store
            .dispatch('rosterStoreModule/updateProfile', {
              id: profileId,
              profileData: {
                status: 'archived',
              },
            })
            .then((response) => {
              this.refetchData();
            });
        }
      });
    },
    /**
     * Whether View Credit has anything to show: transfer credit applied on the
     * profile, or courses credited at enrollment. Listing previous schools alone
     * is not credit.
     */
    hasCreditToView(profile) {
      if (!profile) return false;
      if (profile.toc && profile.toc.enabled) return true;

      // enrollmentToc lists every course of the program; only enabled ones were credited.
      const toc = profile.metadata && profile.metadata.enrollmentToc;
      const courses = (toc && toc.metadata && toc.metadata.courses) || [];
      return courses.some((course) => course && course.enabled);
    },
    /**
     * Whether this profile can have its enrollment accepted.
     *
     * Mirrors the API: submitEnrollmentAcceptance rejects a profile with no
     * metadata.enrollmentId as "Profile does not originate from enrollment", so a
     * profile created directly in management rather than imported from enroll-api
     * has nothing to accept and must not offer the menu at all.
     *
     * Re-accepting is allowed and overwrites the accepted date (DEV-738), but only
     * while the enrollment is in progress: accepting sets the profile back to
     * enrolled, so the API refuses a finished record rather than reopen it.
     *
     * metadata is guarded rather than assumed -- 86 profiles carry no metadata
     * object, and reading through it would throw during render instead of simply
     * evaluating falsy.
     */
    canAcceptEnrollment(profile) {
      if (!this.$can('roster.profile.delete')) return false;
      if (!profile || !profile.metadata || !profile.metadata.enrollmentId) return false;

      return ACCEPTABLE_STATES.includes(profile.status);
    },
    hasEnrollmentAcceptance(profile) {
      return !!(profile && profile.attributes && profile.attributes.acceptanceAt);
    },
    /**
     * Whether the compliance acceptance letter may be sent for this profile.
     *
     * Mirrors letterEligibility() on the API (services/compliance/acceptance-letter.js):
     * the enrollment must be accepted and assigned to a cohort, and retakes, audit
     * and STEM enrollments never receive it (DEV-736). The API enforces the same
     * rule, so this only decides whether to offer the action -- it is not the gate.
     */
    canSendAcceptanceLetter(profile) {
      if (!this.$can('compliance.transcript.create')) return false;
      if (!profile || !profile.attributes || !profile.attributes.acceptanceAt) return false;
      if (!profile.session) return false;
      if (profile.attributes.retake === 'yes') return false;

      return !['audit', 'stem'].includes(profile.attributes.fundingSource);
    },
    /**
     * When the acceptance letter was last sent, or null.
     *
     * metadata is guarded because 86 profiles carry no metadata object at all, and
     * reading through it throws during render rather than evaluating falsy -- the
     * same trap that left the Enrollment submenu broken.
     */
    acceptanceLetterSentAt(profile) {
      return (profile && profile.metadata && profile.metadata.acceptanceLetterSentAt) || null;
    },
    sendAcceptanceLetter(profile) {
      const alreadySent = this.acceptanceLetterSentAt(profile);

      this.$swal({
        title: alreadySent ? 'Send this letter again?' : 'Send the acceptance letter?',
        text: alreadySent
          ? `This student was already sent the acceptance letter on ${new Date(
              alreadySent,
            ).toLocaleDateString()}. Sending again will deliver a second copy.`
          : 'This emails the student their official notice of acceptance, with their signed enrollment agreement attached.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: alreadySent ? 'Send again' : 'Send',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
      }).then(async (result) => {
        if (!result.value) return;

        store
          .dispatch('rosterStoreModule/sendAcceptanceLetter', { profileId: profile.id, force: Boolean(alreadySent) })
          .then((response) => {
            this.refetchData();

            // The API reports what was blank or missing on the rendered letter.
            // Compliance has to see that -- a letter that went out with no program
            // name or no agreement attached cannot be recalled.
            const warnings = (response && response.data && response.data.warnings) || [];

            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Acceptance Letter Sent',
                text: warnings.length ? `Sent with warnings: ${warnings.join('; ')}` : `Sent to ${response.data.to}.`,
                icon: warnings.length ? 'AlertTriangleIcon' : 'CheckCircleIcon',
                variant: warnings.length ? 'warning' : 'success',
              },
            });
          })
          .catch((error) => {
            const message =
              (error.response && error.response.data && error.response.data.message) || 'Failed to send the acceptance letter.';

            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Acceptance Letter Not Sent',
                text: message,
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            });
          });
      });
    },
    submitEnrollmentAcceptance(profile) {
      const profileId = profile.id;
      const previous = this.hasEnrollmentAcceptance(profile) ? momentTz(profile.attributes.acceptanceAt).format('L') : null;

      this.$swal({
        title: 'Are you sure?',
        text: previous
          ? `This enrollment was accepted on ${previous}. Re-accepting replaces that date with today's.`
          : 'Are you sure you want to accept this enrollment?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Accept',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
      }).then(async (result) => {
        if (result.value) {
          store
            .dispatch('rosterStoreModule/submitEnrollmentAcceptance', {
              profileId,
              acceptanceAt: new Date(),
            })
            .then((response) => {
              this.refetchData();

              const { result: accepted, agreementUpdated } = response.data || {};
              let text = 'Enrollment has been accepted.';
              if (!accepted) text = 'Accepted here, but the enrollment system could not be updated.';
              else if (!agreementUpdated) text = 'Accepted, but the countersigned enrollment agreement could not be generated.';

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Enrollment Accepted',
                  text,
                  icon: accepted && agreementUpdated ? 'CheckCircleIcon' : 'AlertTriangleIcon',
                  variant: accepted && agreementUpdated ? 'success' : 'warning',
                },
              });
            })
            .catch((error) => {
              console.error('failed to accept enrollment', error);

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Failed to Accept Enrollment',
                  text: 'An error occurred while accepting the enrollment.',
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              });
            });
        }
      });
    },
    createComplianceTranscript(profileId, type) {
      this.$swal({
        title: 'Create Transcript',
        text: 'Are you sure you want to create this transcript?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Create',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          store
            .dispatch('rosterStoreModule/createComplianceTranscript', {
              profileId,
              type,
            })
            .then((response) => {
              this.refetchData();
            })
            .catch((error) => {
              if (error.response) {
                this.$toast({
                  component: ToastificationContent,

                  props: {
                    variant: 'danger',
                    title: 'Failed to create transcript',
                    text: error.response.data.message,
                  },
                });
                return;
              }

              this.$toast({
                component: ToastificationContent,

                props: {
                  variant: 'danger',
                  title: 'Failed to create transcript',
                  text: 'An unknown error occurred',
                },
              });
            });
        }
      });
    },
    openCourseToCViewModal(profile) {
      this.selectedProfile = profile;
      this.$nextTick(() => (this.isViewCreditOpen = true));
    },
    async openCourseToCModal(profile, isAssign, sectionId) {
      if (!profile.session) {
        this.$toast('Must assign to a cohort before attempting to assign credit');
        return;
      }

      // The credit form lists the cohort's sections by id and name, but this
      // list loads the cohort with bare section ids, which left the form empty.
      // Load this profile's sections (the same data as its section rows).
      let sections = [];
      try {
        const response = await store.dispatch('studentStoreModule/fetchStudentSections', {
          id: this.studentData.id,
          queryParams: { profileIds: [profile.id], populate: 'course', limit: 100, sortBy: 'startDate:asc' },
        });
        sections = response.data.results.map((section) => ({
          id: section.id,
          name: section.name || (section.course && section.course.name) || 'Section',
        }));
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load sections for credit',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response && error.response.data ? error.response.data.message : error.message,
          },
        });
        return;
      }

      this.selectedProfile = profile;
      this.selectedCohort = { ...profile.session, sections };
      this.isAssignOp = isAssign;
      this.selectedCreditSectionId = sectionId;
      this.$nextTick(() => (this.isAddCreditOpen = true));
    },

    async downloadAttendanceSheet(cohort, profile) {
      this.isBtnBusy = true;

      this.$store
        .dispatch('rosterStoreModule/exportProfileAttendance', {
          id: profile.id,
          queryParams: { mode: 'standard' },
        })
        .then((response) => {
          this.isBtnBusy = false;
          const { data } = response;
          fileDownload(data, `${cohort.name} Attendance (${this.studentData.fullName}).xlsx`);
        })
        .catch((error) => {
          this.isBtnBusy = false;
        });
    },
  },
  setup(props) {
    const createProfileActive = ref(false);
    const assignProfileCohortActive = ref(false);
    const cohortProfile = ref(null);

    const { resolveUserRoleVariant } = useUsersList();
    const { resolvePathwayStatusVariant } = usePathways();
    const { resolveCourseStatusVariant } = useCourses();

    const { resolvePathwayCohortName } = usePathwayCohorts();
    const { resolveCourseSectionName } = useCourseSections();

    const { resolveComplianceScoreVariant } = useProfiles();

    const isViewCreditOpen = ref(false);
    const isAddCreditOpen = ref(false);
    const isAssignOp = ref(true);
    const selectedProfile = ref(null);
    const selectedCohort = ref(null);

    const {
      fetchProfiles,
      tableColumnsStudentView,
      perPage,
      currentPage,
      totalProfiles,
      totalPageCount,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refProfileListTable,
      refetchData,

      resolveProfileVisibilityVariant,
      resolveProfileStatusVariant,
      resolveProfileLocationStatus,

      resolveProfilePaymentStatusVariant,
      resolveProfileProgramVariant,
      resolveProfileYNVariant,

      studentFilter,
    } = useProfiles();

    studentFilter.value = props.studentData.id;

    return {
      fetchProfiles,
      tableColumnsStudentView,
      perPage,
      currentPage,
      totalPageCount,
      totalProfiles,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refProfileListTable,
      refetchData,

      // Filter
      avatarText,
      title,

      resolveUserRoleVariant,

      resolveProfileVisibilityVariant,
      resolveProfileStatusVariant,
      resolveProfileLocationStatus,

      resolveProfilePaymentStatusVariant,
      resolveProfileProgramVariant,
      resolveProfileYNVariant,

      resolvePathwayStatusVariant,
      resolveCourseStatusVariant,

      resolveComplianceScoreVariant,
      gradeScore,

      studentFilter,

      resolvePathwayCohortName,
      resolveCourseSectionName,

      createProfileActive,
      assignProfileCohortActive,
      cohortProfile,

      isViewCreditOpen,
      isAddCreditOpen,
      isAssignOp,
      selectedProfile,
      selectedCohort,

      momentTz,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>
